import { memo, useState } from 'react';
import { ListLocationFlagsResponse } from '@weave/schema-gen-ts/dist/schemas/feature-flags/feature_flags_service_api.pb';
import { IntegrationsQueries } from '@frontend/api-integrations';
import { LocationsTypes } from '@frontend/api-locations';
import { useRemoveAppLoader } from '@frontend/document';
import {
  EligibilityReportWritebackModal,
  SaveEligibilityReportProvider,
} from '@frontend/insurance-verification-reports-writeback';
import { useQuery } from '@frontend/react-query-helpers';
import { SchemaFeatureFlags } from '@frontend/schema';
import { ContentLoader, useModalControl } from '@frontend/design-system';
import { Feedback, Footer, GridContainer, Header, InsuranceReportPrint, LimitationsTable } from '../../components';
import { PrintableReport } from '../../components/printable-report';
import { InsuranceReportContainerProvider } from '../../providers/insurance-report-container.provider';
import { InsuranceVerificationReportHeaderContainer } from '../../shared';
import { reportInfoContainerStyles, reportSectionStyles } from '../../styles';
import { InsuranceEligibilityReport } from '../dental-insurance-eligibility-report';
import { DentalInsuranceReportContextProvider } from '../dental-insurance-eligibility-report/context/DentalInsuranceReportDetailsContext.provider';
import { InsuranceReportContextProvider } from '../onederful-insurance-eligibility-report/context/OnederfulReportContext.provider';

type DentalInsuranceEligibilityReportProps = {
  locationId: string;
  personId: string;
  sourceId: string;
  personPMSId: string;
  locationData: LocationsTypes.Location;
  closeInsuranceReportModal: () => void;
  onError?: () => void;
};

export const DentalInsuranceEligibilityReport = memo(
  ({
    locationId,
    personId,
    personPMSId,
    sourceId,
    locationData,
    closeInsuranceReportModal,
    onError,
  }: DentalInsuranceEligibilityReportProps) => {
    const [isPrintDialogVisible, setIsPrintDialogVisible] = useState<boolean>(false);
    const saveEligibilityReportModalProps = useModalControl();
    const { removeAppLoader } = useRemoveAppLoader({ removeOnMount: false });

    const { PMSName, isLoading: isSyncAppDataLoading } = IntegrationsQueries.useIsWritebackSupported({
      locationId,
      sourceId: sourceId,
      disabled: !locationId || !sourceId,
    });

    const { data: featureFlagData, isLoading: isLoadingFeatureFlagDetails } = useQuery({
      queryKey: ['locationId', locationId, 'featureFlag'],
      queryFn: () =>
        SchemaFeatureFlags.ListLocationFlags({
          locationId,
          flagName: 'insurance-verification-report-v2',
        }),
      enabled: !!locationId,
      select: (data: ListLocationFlagsResponse) => {
        const flag = (data.flags || []).find(
          (flag) => flag.flag?.name === 'insurance-verification-report-v2' && flag.value
        );
        return { locationId, flag };
      },
    });

    const isInsuranceVerificationReportV2Enabled = featureFlagData?.flag?.value || false;

    const handleSaveToPMS = () => {
      // Removing the loader during printing the report
      removeAppLoader();
      saveEligibilityReportModalProps.openModal();
    };

    const handlePrint = () => {
      // Removing the loader during printing the report
      removeAppLoader();
      setIsPrintDialogVisible(true);
    };

    if (isLoadingFeatureFlagDetails || isSyncAppDataLoading)
      return <ContentLoader show={true} message={'Loading Insurance Data'} />;

    if (isInsuranceVerificationReportV2Enabled) {
      return (
        <InsuranceReportContainerProvider
          locationId={locationId}
          locationData={locationData}
          personId={personId}
          sourceId={sourceId}
          personPMSId={personPMSId}
          handlePrint={handlePrint}
          handleSaveToPms={handleSaveToPMS}
          onApiFetchFailure={onError}
        >
          <DentalInsuranceReportContextProvider
            hidePrintDialog={() => setIsPrintDialogVisible(false)}
            isPrintDialogVisible={isPrintDialogVisible}
          >
            <InsuranceEligibilityReport
              saveEligibilityReportModalProps={saveEligibilityReportModalProps}
              closeInsuranceReportModal={closeInsuranceReportModal}
            />
          </DentalInsuranceReportContextProvider>
        </InsuranceReportContainerProvider>
      );
    }

    return (
      <InsuranceReportContainerProvider
        locationId={locationId}
        locationData={locationData}
        personId={personId}
        sourceId={sourceId}
        personPMSId={personPMSId}
        handlePrint={handlePrint}
        handleSaveToPms={handleSaveToPMS}
        onApiFetchFailure={onError}
      >
        <InsuranceReportContextProvider>
          <SaveEligibilityReportProvider>
            <InsuranceVerificationReportHeaderContainer />
            <section css={reportSectionStyles}>
              <div css={reportInfoContainerStyles}>
                <Header showFilters={false} handleSaveToPms={handleSaveToPMS} handlePrint={handlePrint} />
                <GridContainer />
                <LimitationsTable />
                <Feedback />
              </div>
            </section>
            <Footer />
            <InsuranceReportPrint
              isPrintDialogVisible={isPrintDialogVisible}
              hidePrintDialog={() => setIsPrintDialogVisible(false)}
            />
            <EligibilityReportWritebackModal
              isInsuranceVerificationReportV2Enabled={isInsuranceVerificationReportV2Enabled}
              PMSName={PMSName}
              locationId={locationId}
              personPMSId={personPMSId}
              sourceId={sourceId}
              mountTarget='#root'
              PrintableReport={<PrintableReport />}
              {...saveEligibilityReportModalProps}
            />
          </SaveEligibilityReportProvider>
        </InsuranceReportContextProvider>
      </InsuranceReportContainerProvider>
    );
  }
);

DentalInsuranceEligibilityReport.displayName = 'DentalInsuranceEligibilityReport';
